import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { 
  Bitcoin, 
  DollarSign, 
  TrendingUp,
  Users,
  Activity,
  LogOut,
  ArrowLeft,
  Shield,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is admin
    if (user && !user.is_admin) {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    
    fetchAdminData();
  }, [user]);

  const fetchAdminData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, businessesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/businesses`, { headers })
      ]);
      
      setStats(statsRes.data);
      setBusinesses(businessesRes.data);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to fetch admin data';
      toast.error(errorMsg);
      
      if (error.response?.status === 403) {
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const handleGenerateInvoice = (business) => {
    toast.info(`Manual invoice generation for ${business.business_name} - Coming soon!`);
  };

  const handlePromoteUser = async (business) => {
    if (!window.confirm(`Promote ${business.business_name} to admin?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.post(`${API}/admin/promote-user/${business.user_id}`, {}, { headers });
      toast.success(`${business.business_name} promoted to admin`);
      fetchAdminData(); // Refresh data
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to promote user';
      toast.error(errorMsg);
    }
  };

  const handleDemoteUser = async (business) => {
    if (!window.confirm(`Demote ${business.business_name} from admin?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.post(`${API}/admin/demote-user/${business.user_id}`, {}, { headers });
      toast.success(`${business.business_name} demoted from admin`);
      fetchAdminData(); // Refresh data
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to demote user';
      toast.error(errorMsg);
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-page">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-container">
          <div className="header-left">
            <div className="logo">
              <Bitcoin size={32} />
              <span>Crypto Cashier Admin</span>
            </div>
          </div>
          <div className="header-right">
            <div className="admin-badge">
              <Shield size={16} />
              Admin
            </div>
            <div className="user-info">
              <span className="user-name">{user?.business_name}</span>
              <span className="user-email">{user?.email}</span>
            </div>
            <button 
              className="btn btn-secondary" 
              onClick={handleLogout}
            >
              <LogOut size={18} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="dashboard-content">
        <div className="dashboard-container">
          {/* Back to Dashboard */}
          <button 
            className="back-btn"
            onClick={() => navigate('/dashboard')}
          >
            <ArrowLeft size={18} /> Back to Dashboard
          </button>

          {/* Page Title */}
          <div className="page-header">
            <Shield size={32} />
            <h1>Platform Administration</h1>
            <p>Manage all businesses and track platform revenue</p>
          </div>

          {/* Platform Stats */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon" style={{background: 'rgba(96, 165, 250, 0.1)'}}>
                <Users size={24} color="#60a5fa" />
              </div>
              <div className="stat-info">
                <p className="stat-label">Total Businesses</p>
                <p className="stat-value">{stats?.total_businesses || 0}</p>
                <p className="stat-description">{stats?.active_businesses || 0} active</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon" style={{background: 'rgba(34, 197, 94, 0.1)'}}>
                <DollarSign size={24} color="#22c55e" />
              </div>
              <div className="stat-info">
                <p className="stat-label">Platform Revenue</p>
                <p className="stat-value">${stats?.total_platform_revenue?.toFixed(2) || '0.00'}</p>
                <p className="stat-description">Total owed by all businesses</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon" style={{background: 'rgba(251, 191, 36, 0.1)'}}>
                <TrendingUp size={24} color="#fbbf24" />
              </div>
              <div className="stat-info">
                <p className="stat-label">Transaction Fees (1%)</p>
                <p className="stat-value">${stats?.total_transaction_fees_owed?.toFixed(2) || '0.00'}</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon" style={{background: 'rgba(168, 85, 247, 0.1)'}}>
                <Activity size={24} color="#a855f7" />
              </div>
              <div className="stat-info">
                <p className="stat-label">Subscription Fees</p>
                <p className="stat-value">${stats?.total_subscription_revenue_owed?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          </div>

          {/* Businesses Table */}
          <div className="businesses-section">
            <h2 className="section-title">
              <FileText size={20} />
              All Businesses
            </h2>
            
            <div className="businesses-table-container">
              {businesses.length === 0 ? (
                <div className="empty-state">
                  <Users size={48} color="rgba(228, 230, 235, 0.3)" />
                  <p>No businesses registered yet</p>
                </div>
              ) : (
                <table className="businesses-table">
                  <thead>
                    <tr>
                      <th>Business Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Transaction Volume</th>
                      <th>Platform Fees (1%)</th>
                      <th>Subscription</th>
                      <th>Total Owed</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {businesses.map((business, index) => (
                      <tr key={business.user_id}>
                        <td className="business-name">
                          {business.business_name}
                          {business.user_id === user?.id && (
                            <span className="you-badge">You</span>
                          )}
                        </td>
                        <td className="business-email">{business.email}</td>
                        <td>
                          {business.is_admin ? (
                            <span className="role-badge admin-role">Admin</span>
                          ) : (
                            <span className="role-badge user-role">User</span>
                          )}
                        </td>
                        <td>${business.total_transaction_volume?.toFixed(2) || '0.00'}</td>
                        <td>${business.platform_fees_accumulated?.toFixed(2) || '0.00'}</td>
                        <td>
                          ${business.subscription_fee_owed?.toFixed(2) || '0.00'}
                          <span className="months-badge">{business.months_active}mo</span>
                        </td>
                        <td className="total-owed-cell">
                          ${business.total_owed?.toFixed(2) || '0.00'}
                        </td>
                        <td>
                          <span className={`status-badge status-${business.subscription_status}`}>
                            {business.subscription_status}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button 
                              className="btn-small btn-primary"
                              onClick={() => handleGenerateInvoice(business)}
                            >
                              Invoice
                            </button>
                            {business.user_id !== user?.id && (
                              business.is_admin ? (
                                <button 
                                  className="btn-small btn-danger"
                                  onClick={() => handleDemoteUser(business)}
                                >
                                  Demote
                                </button>
                              ) : (
                                <button 
                                  className="btn-small btn-success"
                                  onClick={() => handlePromoteUser(business)}
                                >
                                  Promote
                                </button>
                              )
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Invoice Notice */}
          <div className="notice-card">
            <div className="notice-icon">📄</div>
            <div className="notice-content">
              <h3>Manual Invoice Generation</h3>
              <p>
                Use the "Invoice" button to manually generate invoices for individual businesses.
                The system calculates accumulated platform fees (1% of transactions) and monthly subscription fees ($10/month).
              </p>
              <p className="notice-subtext">
                Admin Management: Use "Promote" to grant admin privileges or "Demote" to remove them. You cannot demote yourself.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
